import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { VehicleGeneralFigureService } from 'src/app/_services/vehicle-general-figure.service';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-regenerative-charging-chart',
  templateUrl: './regenerative-charging-chart.component.html',
  styleUrls: ['./regenerative-charging-chart.component.scss']
})
export class RegenerativeChargingChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles!: any[];
  deviceSerialNumber: string[] = [];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  numberMonths: number = 0;
  private colorMap: { [key: string]: string } = {};

  constructor(
    private readonly vehicleGeneralFigureService: VehicleGeneralFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currentValue = changes['selectedVehicles'] && changes['selectedVehicles'].currentValue;
    if (Array.isArray(currentValue) && currentValue.length > 0) {
      this.dataLoading = true;
      this.errorMessage = false;
      this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
      this.vehicleGeneralFigureService.getRegenerativeCharging(this.deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (data) => {
          this.initializeColorMap();
          this.plotRecuperationEnergyChart(data);
          this.dataLoading = false;
        },
        error: (error) => {
          console.error('Error fetching Recuperated Energy data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      });
    } else {
      this.dataLoading = false;
      this.errorMessage = true;
    }
  }

  plotRecuperationEnergyChart(data: any): void {
    const plotData = Object.keys(data).map((deviceSerialNumberId: string) => {
      const deviceDataArray = this.sortByTime(data[deviceSerialNumberId]);
      const vehicle = this.selectedVehicles.find(vehicle => vehicle.batteryIdentifier === deviceSerialNumberId);

      // Map the recuperation energy data
      const months = deviceDataArray.map((dataObject: any) => {
        const date = new Date(dataObject.month);
        return date.toLocaleString('default', { month: 'short', year: 'numeric' });
      });

      this.numberMonths = months.length

      const energies = deviceDataArray.map((dataObject: any) => dataObject.energy);

      return {
        x: months,
        y: energies,
        type: 'bar',
        name: vehicle ? vehicle.name : `Vehicle ${deviceSerialNumberId}`,
        marker: {
          color: this.colorMap[deviceSerialNumberId]
        }
      };
    });

    const layout = {
      title: 'Recuperated Energy during Driving',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'Time [Months]',
      },
      yaxis: {
        title: 'Energy [kWh]'
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('regenerative-charging', plotData, layout);
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

  sortByTime(data: any[]): any[] {
    return data.slice().sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
  }
}
